import type { ReactNode } from 'react';
import logoDark from '@/assets/brand/orbiter-logo-dark.svg';
import logoLight from '@/assets/brand/orbiter-logo.svg';
import markLight from '@/assets/brand/orbiter-mark.svg';
import { BRAND } from '@/lib/brand';

/** Width over height of each drawing's viewBox. */
const RATIO = { full: 316.88 / 69.58, mark: 115.06 / 69.58 } as const;

/**
 * The product's logo: the mark and the name (`full`), or the mark alone
 * (`mark`, for the folded sidebar).
 *
 * Each theme has its own file, and both are in the page: the stylesheet shows
 * the one for `<html data-theme>`, which public/theme.js sets before the first
 * paint. The right file is there from the first frame, and switching themes
 * swaps two boxes of the same size: nothing moves and nothing has to load.
 *
 * There is no dark file of the mark alone. The dark logo cut to the mark's
 * width is that mark: orbiter-mark.svg is the left part of orbiter-logo.svg to
 * the unit, and the dark logo is drawn on the same grid.
 */
export function Logo({
  variant = 'full',
  height,
  className,
}: {
  readonly variant?: 'full' | 'mark';
  /** In CSS pixels; the width follows from the drawing. */
  readonly height: number;
  readonly className?: string;
}): ReactNode {
  const width = Math.round(height * RATIO[variant]);

  return (
    <span className={className ? `logo ${className}` : 'logo'}>
      <img
        className="logo-light"
        src={variant === 'full' ? logoLight : markLight}
        alt={BRAND.name}
        width={width}
        height={height}
      />
      <img
        className={variant === 'full' ? 'logo-dark' : 'logo-dark logo-crop'}
        src={logoDark}
        alt={BRAND.name}
        width={width}
        height={height}
      />
    </span>
  );
}
